import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  addressValidator,
  fullNameValidator,
  strictNameValidator,
  zipCodeValidator,
} from '@/constants/validationRules';
import { getNameErrorMessage } from '@/utility/getNameErrorMessage';
import {
  classifyFullName,
  classifyName,
  ENameRejection,
  splitFullName,
  toCanonicalName,
} from '@/utility/nameValidation';

export const useRequiredInputRules = () => {
  const intl = useIntl();

  return useCallback(
    (field: string) => {
      return {
        required: {
          value: true,
          message: intl.formatMessage(
            {
              defaultMessage: '{field} is required',
              id: '5UFa/G',
              description: 'Required field error label',
            },
            { field },
          ),
        },
      };
    },
    [intl],
  );
};

export const useMinLengthInputRules = () => {
  const intl = useIntl();

  return useCallback(
    (field: string, minLength: number) => {
      return {
        minLength: {
          value: minLength,
          message: intl.formatMessage(
            {
              defaultMessage: '{field} must be at least {minLength} characters long',
              id: '+ES0Lw',
              description: 'Min length field error label',
            },
            { field, minLength },
          ),
        },
      };
    },
    [intl],
  );
};

export const useMaxLengthInputRules = () => {
  const intl = useIntl();

  return useCallback(
    (field: string, maxLength: number) => {
      return {
        maxLength: {
          value: maxLength,
          message: intl.formatMessage(
            {
              defaultMessage: '{field} must be at most {maxLength} characters long',
              id: 'fTOugc',
              description: 'Max length field error label',
            },
            { field, maxLength },
          ),
        },
      };
    },
    [intl],
  );
};

export const useAddressInputRules = () => {
  const intl = useIntl();

  const buildRequiredInputRules = useRequiredInputRules();
  const buildMinLengthInputRules = useMinLengthInputRules();
  const buildMaxLengthInputRules = useMaxLengthInputRules();

  return useCallback(
    (field: string, nullable?: boolean) => {
      return {
        ...(nullable ? {} : buildRequiredInputRules(field)),
        ...buildMinLengthInputRules(field, addressValidator.minLength),
        ...buildMaxLengthInputRules(field, addressValidator.maxLength),
        pattern: {
          value: addressValidator.pattern,
          message: intl.formatMessage({
            defaultMessage:
              'No special characters allowed other than dot, comma, dash and apostrophe',
            id: 'PFtu0d',
            description: 'Address field pattern error',
          }),
        },
      };
    },
    [intl, buildRequiredInputRules, buildMinLengthInputRules, buildMaxLengthInputRules],
  );
};

export const useZipCodeInputRules = () => {
  const intl = useIntl();

  const buildRequiredInputRules = useRequiredInputRules();
  const buildMinLengthInputRules = useMinLengthInputRules();
  const buildMaxLengthInputRules = useMaxLengthInputRules();

  return useCallback(
    (field: string) => {
      return {
        ...buildRequiredInputRules(field),
        ...buildMinLengthInputRules(field, zipCodeValidator.minLength),
        ...buildMaxLengthInputRules(field, zipCodeValidator.maxLength),
        pattern: {
          value: zipCodeValidator.pattern,
          message: intl.formatMessage({
            defaultMessage:
              'No special characters allowed other than dot, comma, dash and apostrophe',
            id: 'PFtu0d',
            description: 'Address field pattern error',
          }),
        },
      };
    },
    [intl, buildRequiredInputRules, buildMinLengthInputRules, buildMaxLengthInputRules],
  );
};

// Every rule in this hook measures the same canonical string that gets
// persisted — trimming inside `onChange` isn't an option (the user has to be
// able to type the space in "Mary Jane") and RHF's `Controller` doesn't expose
// `setValueAs`, so a `validate` transform is the only place the raw field
// value can be canonicalized before rules run.
export const useStrictNameInputRules = () => {
  const intl = useIntl();
  const buildRequiredInputRules = useRequiredInputRules();

  // maxLength override exists because the default (40) matches checkout, but
  // profile edit stores legal names that already exceed it in prod — the API
  // column is unbounded text. minLength override exists for the blocking
  // romanized-name gate, which must not be stricter than the server.
  return useCallback(
    (
      field: string,
      maxLength: number = strictNameValidator.maxLength,
      minLength: number = strictNameValidator.minLength,
    ) => {
      const requiredMessage = intl.formatMessage(
        {
          defaultMessage: '{field} is required',
          id: '5UFa/G',
          description: 'Required field error label',
        },
        { field },
      );
      const minLengthMessage = intl.formatMessage(
        {
          defaultMessage: '{field} must be at least {minLength} characters long',
          id: '+ES0Lw',
          description: 'Min length field error label',
        },
        { field, minLength },
      );
      const maxLengthMessage = intl.formatMessage(
        {
          defaultMessage: '{field} must be at most {maxLength} characters long',
          id: 'fTOugc',
          description: 'Max length field error label',
        },
        { field, maxLength },
      );
      return {
        ...buildRequiredInputRules(field),
        validate: (value: string) => {
          const canonical = toCanonicalName(value ?? '');
          if (canonical.length === 0) return requiredMessage;
          if (canonical.length < minLength) return minLengthMessage;
          if (canonical.length > maxLength) return maxLengthMessage;
          // Two causes, two approved strings — a guest who typed 李雷 is told to
          // use Latin letters, not that they used a special character.
          return getNameErrorMessage(intl, classifyName(canonical)) ?? true;
        },
      };
    },
    [intl, buildRequiredInputRules],
  );
};

/**
 * Rules for a combined "Full name" field — signup and the guest-invite forms.
 *
 * Applies the same Latin-script policy as `useStrictNameInputRules`, but split
 * first: the server stores first and last separately and reports whichever fails
 * first, so classifying the whole string would disagree with it on mixed inputs
 * like "Иван Bob5". See `classifyFullName`.
 *
 * The required/length copy is overridable because these three surfaces word it
 * differently ("Full name is required" vs "Please enter the guest's full name").
 * The two Latin-script strings are not overridable — they are approved copy and
 * shared on purpose.
 */
export const useFullNameInputRules = () => {
  const intl = useIntl();

  return useCallback(
    ({
      requiredMessage,
      minLengthMessage,
      maxLengthMessage,
      lastNameMessage,
    }: {
      requiredMessage: string;
      minLengthMessage: string;
      maxLengthMessage: string;
      /** Shown when only one token was entered — the two-word rule for a combined field. */
      lastNameMessage: string;
    }) => ({
      required: { value: true, message: requiredMessage },
      validate: (value: string) => {
        const canonical = toCanonicalName(value ?? '');
        if (canonical.length === 0) return requiredMessage;
        if (canonical.length < fullNameValidator.minLength) return minLengthMessage;
        if (canonical.length > fullNameValidator.maxLength) return maxLengthMessage;

        // Script/character check runs BEFORE the two-token check on purpose. A
        // single-token non-Latin entry like "李雷" fails both, and "enter your
        // name in Latin letters" is the useful half — telling someone who typed
        // a name that their name is required just reads as a broken form.
        const rejection = classifyFullName(canonical);
        if (rejection !== ENameRejection.Ok) {
          return getNameErrorMessage(intl, rejection) ?? lastNameMessage;
        }

        if (!splitFullName(canonical).lastName) return lastNameMessage;
        return true;
      },
    }),
    [intl],
  );
};
